import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Stack,
  IconButton,
  CircularProgress,
  Paper,
  TextField,
  MenuItem,
  Divider,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  KingBed as KingBedIcon,
  Person as PersonIcon,
  Bed as BedIcon,
  CheckCircle as CheckCircleIcon,
  Square as SquareIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { fetchRooms, type Room } from '../services/roomService'
import { checkRoomAvailability } from '../services/bookingService'
import BookingContactModal from './BookingContactModal'
import { amenityIcons } from './Rooms' // You'll need to export this from Rooms.tsx

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const roomTypes = [
  'all',
  'Deluxe',
  'Super Deluxe',
  'Family Suite',
  'Deluxe Non-AC',
];

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [currentImageIndexes, setCurrentImageIndexes] = useState<{ [key: string]: number }>({})
  const [openBooking, setOpenBooking] = useState(false)
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null)
  const [isEditingSearch, setIsEditingSearch] = useState(false)
  const [tempSearchCriteria, setTempSearchCriteria] = useState({
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    adults: parseInt(searchParams.get('adults') || '1'),
    children: parseInt(searchParams.get('children') || '0'),
    roomType: searchParams.get('roomType') || 'all'
  })

  // Get search parameters
  const [searchCriteria, setSearchCriteria] = useState({
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    adults: parseInt(searchParams.get('adults') || '1'),
    children: parseInt(searchParams.get('children') || '0'),
    roomType: searchParams.get('roomType') || 'all'
  })

  const loadRooms = async (criteria: typeof searchCriteria) => {
    try {
      setLoading(true)
      const roomsData = await fetchRooms()
      
      // Filter rooms based on initial criteria
      let filteredRooms = roomsData.filter(room => {
        if (criteria.roomType !== 'all' && room.type !== criteria.roomType) return false
        
        // Filter by capacity
        const hasEnoughCapacity = 
          room.capacity.adults >= criteria.adults && 
          room.capacity.children >= criteria.children
        
        return hasEnoughCapacity
      })

      // Check availability for each room
      const availabilityChecks = await Promise.all(
        filteredRooms.map(async (room) => {
          const checkIn = new Date(criteria.checkIn)
          const checkOut = new Date(criteria.checkOut)
          
          // Skip availability check if dates are invalid
          if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) {
            return { room, available: true }
          }

          const { available } = await checkRoomAvailability(
            room.id,
            checkIn,
            checkOut
          )
          return { room, available }
        })
      )

      // Filter out unavailable rooms
      filteredRooms = availabilityChecks
        .filter(({ available }) => available)
        .map(({ room }) => room)

      setRooms(filteredRooms)
    } catch (error) {
      console.error('Error loading rooms:', error)
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    loadRooms(searchCriteria)
  }, []) // Empty dependency array for initial load only

  const handleSearchUpdate = (event: React.FormEvent) => {
    event.preventDefault()
    
    // Update search params
    const params = new URLSearchParams()
    Object.entries(tempSearchCriteria).forEach(([key, value]) => {
      params.set(key, value.toString())
    })
    setSearchParams(params)
    
    // Update actual search criteria and trigger search
    setSearchCriteria(tempSearchCriteria)
    loadRooms(tempSearchCriteria)
    setIsEditingSearch(false)
  }

  const handleNextImage = (roomId: string) => {
    setCurrentImageIndexes(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + 1) % rooms.find(r => r.id === roomId)!.images.length
    }))
  }

  const handlePrevImage = (roomId: string) => {
    setCurrentImageIndexes(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) - 1 + rooms.find(r => r.id === roomId)!.images.length) % rooms.find(r => r.id === roomId)!.images.length
    }))
  }

  const [contactModalRoom, setContactModalRoom] = useState<Room | null>(null)

  const handleBookNow = (room: Room) => {
    setContactModalRoom(room)
  }

  const handleViewDetails = (room: Room) => {
    navigate(`/rooms/${room.id}`, {
      state: {
        bookingDetails: {
          checkIn: searchCriteria.checkIn,
          checkOut: searchCriteria.checkOut,
          adults: searchCriteria.adults,
          children: searchCriteria.children
        }
      }
    })
  }

  return (
    <Box>
      <BookingContactModal
        open={contactModalRoom !== null}
        onClose={() => setContactModalRoom(null)}
        roomName={contactModalRoom?.name}
      />
      {/* Search Results Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 3, md: 4 },
          mt: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <IconButton
              onClick={() => navigate('/rooms')}
              sx={{ 
                color: 'primary.main',
                '&:hover': { bgcolor: 'primary.50' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                Search Results
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'} found for your search
              </Typography>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.default',
            }}
          >
            {!isEditingSearch ? (
              <>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Check-in
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(searchCriteria.checkIn).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Check-out
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(searchCriteria.checkOut).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Guests
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {searchCriteria.adults} Adults, {searchCriteria.children} Children
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Room Type
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {searchCriteria.roomType === 'all' ? 'All Types' : searchCriteria.roomType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditingSearch(true)}
                      variant="outlined"
                      fullWidth
                      sx={{ mt: { xs: 2, md: 0 } }}
                    >
                      Modify Search
                    </Button>
                  </Grid>
                </Grid>
              </>
            ) : (
              <form onSubmit={handleSearchUpdate}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Check-in
                    </Typography>
                    <input
                      type="date"
                      value={tempSearchCriteria.checkIn}
                      onChange={(e) => setTempSearchCriteria(prev => ({ ...prev, checkIn: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Check-out
                    </Typography>
                    <input
                      type="date"
                      value={tempSearchCriteria.checkOut}
                      onChange={(e) => setTempSearchCriteria(prev => ({ ...prev, checkOut: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }}
                      min={tempSearchCriteria.checkIn}
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Adults
                    </Typography>
                    <input
                      type="number"
                      value={tempSearchCriteria.adults}
                      onChange={(e) => setTempSearchCriteria(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }}
                      min="1"
                      max="6"
                      required
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Children
                    </Typography>
                    <input
                      type="number"
                      value={tempSearchCriteria.children}
                      onChange={(e) => setTempSearchCriteria(prev => ({ ...prev, children: parseInt(e.target.value) }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }}
                      min="0"
                      max="4"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Room Type
                    </Typography>
                    <select
                      value={tempSearchCriteria.roomType}
                      onChange={(e) => setTempSearchCriteria(prev => ({ ...prev, roomType: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      }}
                    >
                      {roomTypes.map((type) => (
                        <option key={type} value={type}>
                          {type === 'all' ? 'All Types' : type}
                        </option>
                      ))}
                    </select>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button
                        onClick={() => setIsEditingSearch(false)}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SearchIcon />}
                      >
                        Update Search
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Results Section */}
      <Box
        component={motion.div}
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={staggerContainer}
        sx={{
          py: { xs: 4, md: 6 },
          bgcolor: 'background.default',
        }}
      >
        <Container maxWidth="xl">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : rooms.length > 0 ? (
            <Stack spacing={4}>
              {rooms.map((room) => (
                <Box
                  key={room.id}
                  component={motion.div}
                  variants={fadeInUp}
                  sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Grid container>
                    {/* Left side - Image */}
                    <Grid item xs={12} sm={4} md={3}>
                      <Box sx={{ position: 'relative', height: '100%', minHeight: { xs: 200, sm: '100%' } }}>
                        <CardMedia
                          component="img"
                          sx={{
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          image={room.images[currentImageIndexes[room.id] || 0]}
                          alt={room.name}
                        />
                        {/* Image Navigation */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 1,
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 80%, rgba(0,0,0,0.3) 100%)',
                            '&:hover': {
                              opacity: 1,
                            },
                          }}
                        >
                          <IconButton
                            onClick={() => handlePrevImage(room.id)}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'white',
                              },
                            }}
                          >
                            <NavigateBeforeIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleNextImage(room.id)}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'white',
                              },
                            }}
                          >
                            <NavigateNextIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        {/* Status Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: room.status === 'available' ? 'success.main' : 'error.main',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          }}
                        >
                          {room.status === 'available' ? 'Available' : 'Booked'}
                        </Box>
                      </Box>
                    </Grid>

                    {/* Right side - Content */}
                    <Grid item xs={12} sm={8} md={9}>
                      <Box sx={{ 
                        p: 2.5,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                        <Grid container spacing={2}>
                          {/* Header & Price */}
                          <Grid item xs={12} sm={8}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                mb: 0.5,
                              }}
                            >
                              {room.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {room.type}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
                            <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                              {room.discountedPrice ? (
                                <>
                                  <Typography 
                                    variant="h6" 
                                    color="error.main"
                                    sx={{ 
                                      fontWeight: 700,
                                      fontSize: '1.25rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    {convertPrice(room.discountedPrice)}
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{ 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                      }}
                                    >
                                      / night
                                    </Typography>
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ 
                                      color: 'text.secondary',
                                      textDecoration: 'line-through',
                                      fontWeight: 500
                                    }}
                                  >
                                    {convertPrice(room.basePrice)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ 
                                      color: 'success.main',
                                      fontWeight: 600,
                                      mt: 0.5
                                    }}
                                  >
                                    Save {Math.round((1 - room.discountedPrice / room.basePrice) * 100)}%
                                  </Typography>
                                </>
                              ) : (
                                <Typography 
                                  variant="h6" 
                                  color="primary.main"
                                  sx={{ 
                                    fontWeight: 700,
                                    fontSize: '1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}
                                >
                                  {convertPrice(room.basePrice)}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ 
                                      color: 'text.secondary',
                                      fontWeight: 500
                                    }}
                                  >
                                    / night
                                  </Typography>
                                </Typography>
                              )}
                            </Stack>
                           </Grid>

                          {/* Quick Info */}
                          <Grid item xs={12}>
                            <Stack 
                              direction="row" 
                              spacing={3}
                              divider={<Divider orientation="vertical" flexItem />}
                              sx={{ mb: 2 }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  bgcolor: 'primary.50',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <SquareIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
                                </Box>
                                <Typography variant="body2">{room.size.squareFeet} sq.ft</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  bgcolor: 'primary.50',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <PersonIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
                                </Box>
                                <Typography variant="body2">
                                  {room.capacity.adults} Adults + {room.capacity.children} Children
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  bgcolor: 'primary.50',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}>
                                  <KingBedIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
                                </Box>
                                <Typography variant="body2">
                                  {room.beds
                                    .filter(bed => bed.count > 0)
                                    .map(bed => `${bed.count} ${bed.type}`)
                                    .join(', ')}
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>

                          {/* Amenities */}
                          <Grid item xs={12}>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Room Amenities
                            </Typography>
                            <Stack 
                              direction="row" 
                              flexWrap="wrap" 
                              gap={1} 
                              sx={{ mb: 2 }}
                            >
                              {room.amenities.map((amenity) => (
                                <Chip
                                  key={amenity}
                                  icon={amenityIcons[amenity]}
                                  label={amenity}
                                  size="small"
                                  sx={{
                                    height: 28,
                                    bgcolor: 'grey.50',
                                    '&:hover': {
                                      bgcolor: 'grey.100',
                                    },
                                    '& .MuiChip-icon': {
                                      color: 'primary.main',
                                      fontSize: '1rem',
                                      ml: 0.5,
                                    },
                                    '& .MuiChip-label': {
                                      px: 1,
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              ))}
                            </Stack>
                          </Grid>

                          {/* Actions */}
                          <Grid item xs={12}>
                            <Stack 
                              direction="row" 
                              spacing={1}
                              justifyContent="flex-end"
                              sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                pt: 2,
                              }}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleBookNow(room)}
                                sx={{
                                  minWidth: '90px',
                                  py: 0.75,
                                  px: 2,
                                  fontSize: '0.875rem',
                                }}
                              >
                                Book
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewDetails(room)}
                                sx={{
                                  minWidth: '90px',
                                  py: 0.75,
                                  px: 2,
                                  fontSize: '0.875rem',
                                }}
                              >
                                View
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h5" gutterBottom>
                No rooms available for your search criteria
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your search parameters or dates
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate('/rooms')}
                sx={{ mt: 3 }}
              >
                Modify Search
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    </Box>
  )
}

export default SearchResults 